// apps/client/src/state/slices/notifications.ts (M1-T18) — web push opt-in (spec §10.4); secure contexts only (§8.10)
import { tuning } from '@whiskerton/content';
import { api as realApi, type Api } from '../../api/endpoints';
import type { SliceCreator } from '../types';

interface PushSubLike { toJSON(): { endpoint?: string; keys?: Record<string, string> } }
interface WinLike { isSecureContext?: boolean; PushManager?: unknown; Notification?: { requestPermission(): Promise<string>; permission?: string } | unknown;
  navigator?: { serviceWorker?: { ready: Promise<{ pushManager: { subscribe(o: object): Promise<PushSubLike> } }> } } }
export interface NotificationDeps { win(): WinLike; api: Pick<Api, 'subscribePush' | 'putPushPrefs' | 'unsubscribePush'>; timeZone(): string }
let deps: NotificationDeps = { win: () => globalThis as unknown as WinLike, api: realApi, timeZone: () => Intl.DateTimeFormat().resolvedOptions().timeZone };
/** Tests inject the window and the API. */
export function setNotificationDeps(d: Partial<NotificationDeps>): void { deps = { ...deps, ...d }; }
export function pushSupported(w: { isSecureContext?: boolean; PushManager?: unknown; Notification?: unknown; navigator?: { serviceWorker?: unknown } }): boolean { return !!w.isSecureContext && !!w.PushManager && !!w.Notification && !!w.navigator?.serviceWorker; }
/** The browser's notification permission ('default' | 'granted' | 'denied'), or null where there is none. */
export function notificationPermission(): string | null {
  const n = deps.win().Notification as { permission?: string } | undefined;
  return n?.permission ?? null;
}
const SUB_KEY = 'wt:pushSub', QUIET_KEY = 'wt:pushQuiet';
const isMinute = (n: unknown) => Number.isInteger(n) && (n as number) >= 0 && (n as number) < 1440;
/** This device's quiet window (it is per subscription on the server), remembered so the pickers show what the server has. */
function loadQuiet(): { start: number; end: number } {
  const fallback = { start: tuning.push.quietStart, end: tuning.push.quietEnd };
  try {
    const q = JSON.parse(localStorage.getItem(QUIET_KEY) ?? 'null') as { start?: unknown; end?: unknown } | null;
    return q && isMinute(q.start) && isMinute(q.end) ? { start: q.start as number, end: q.end as number } : fallback;
  } catch { return fallback; }
}
const keyBytes = (b64: string) => Uint8Array.from(atob(b64.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((b64.length + 3) % 4)), (c) => c.charCodeAt(0));

export interface NotificationsSlice {
  notificationsSupported: boolean; notificationsEnabled: boolean; notificationsQuiet: { start: number; end: number };
  notificationsEnable(): Promise<boolean>; notificationsDisable(): Promise<void>; notificationsSetQuiet(start: number, end: number): Promise<void>;
}
// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- declaration merging into GameStore
declare module '../types' { interface GameStore extends NotificationsSlice {} }

export const slice: SliceCreator<NotificationsSlice> = (set, get) => ({
  notificationsSupported: pushSupported(deps.win()),
  notificationsEnabled: (() => { try { return !!localStorage.getItem(SUB_KEY); } catch { return false; } })(),
  notificationsQuiet: loadQuiet(),
  async notificationsEnable() {
    const w = deps.win();
    if (!pushSupported(w)) { set({ notificationsSupported: false }); return false; }
    if ((await (w.Notification as { requestPermission(): Promise<string> }).requestPermission()) !== 'granted') return false;
    const key = get().config?.vapidPublicKey;
    if (!key) return false;
    const reg = await w.navigator!.serviceWorker!.ready;
    const sub = (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key) })).toJSON();
    if (!sub.endpoint || !sub.keys?.p256dh || !sub.keys.auth) return false;
    const q = get().notificationsQuiet;
    const { id } = await deps.api.subscribePush({ endpoint: sub.endpoint, keys: { p256dh: sub.keys.p256dh, auth: sub.keys.auth }, timeZone: deps.timeZone(), quietStart: q.start, quietEnd: q.end });
    try { localStorage.setItem(SUB_KEY, id); } catch { /* storage may be blocked */ }
    set({ notificationsEnabled: true });
    return true;
  },
  async notificationsDisable() {
    let id: string | null = null;
    try { id = localStorage.getItem(SUB_KEY); localStorage.removeItem(SUB_KEY); } catch { /* storage may be blocked */ }
    set({ notificationsEnabled: false });
    if (id) await deps.api.unsubscribePush(id);
  },
  async notificationsSetQuiet(start, end) {
    set({ notificationsQuiet: { start, end } });
    try { localStorage.setItem(QUIET_KEY, JSON.stringify({ start, end })); } catch { /* storage may be blocked */ }
    if (get().notificationsEnabled) await deps.api.putPushPrefs({ enabled: true, timeZone: deps.timeZone(), quietStart: start, quietEnd: end });
  },
});
